
import {
  Component,
  Mixins,
  Provide,
  Prop,
  Watch
} from "vue-property-decorator";
import { CreateElement } from "vue";
import { mapState } from "vuex";
import _ from "@/boot/lodash";

// Mixins
import Context from "@/mixins/context";
import RequestPromises from "@/mixins/requestPromises";

// Enums/Data
import { DataModules } from "@/store/modules/data";
import { BrandConfigKeys } from "@/data/constants";

// Typings
import { IState } from "@/store";
import { IBrand } from "@/models/brands";
import { IAttachment } from "@/models/uploadFiles";
import {
  IFilesProviderData,
  IFilesProviderMethods
} from "@/models/providers/files";
import { IProvider } from "@/models/providers";

@Component({
  computed: {
    ...mapState({
      brandConfigurationScope(state: IState, getters) {
        return getters[`data/${DataModules.BRANDS}/configurationScope`](
          this.brandId
        );
      },
      brandConfigurationData(state: IState, getters) {
        return (
          getters[`data/single`]({
            storeModule: DataModules.BRANDS,
            scope: this.brandConfigurationScope
          }) || {}
        );
      }
    })
  }
})
export default class FilesProvider extends Mixins(Context, RequestPromises)
  implements IProvider {
  public brandConfigurationData!: IBrand;
  public brandConfigurationScope!: string;

  // Props
  @Prop({ required: true })
  public brandId!: IBrand["id"];

  @Prop({ default: false })
  public handleLoading!: boolean;

  @Prop({ default: () => ({}) })
  public loaderAttrs!: Record<string, any>;

  @Prop({ default: false })
  public handleRequestError!: boolean;

  // Data
  public isLoading = true;
  public isReloading = false;
  public hasRequestError = false;

  public files: File[] = [];
  public attachments: IAttachment[] = [];

  @Provide("filesProviderData")
  public providerData(): IFilesProviderData {
    return {
      attachments: this.attachments,
      fileTypes: this.fileTypes,
      files: this.files,
      hasRequestError: this.hasRequestError,
      hasValidAttachments: this.hasValidAttachments,
      isLoading: this.isLoading,
      isReloading: this.isReloading
    };
  }

  @Provide("filesProviderMethods")
  public get providerMethods(): IFilesProviderMethods {
    return {
      removeAttachment: this.removeAttachment,
      isAllowed: this.isAllowed,
      hasWrongSize: this.hasWrongSize,
      reloadData: this.reloadData
    };
  }

  public created() {
    this.loadData();
  }

  public async loadData() {
    try {
      await this.getBrandConfiguration(this.isReloading);

      this.hasRequestError = false;
    } catch (error) {
      this.hasRequestError = true;
      this.$store.dispatch("api/handleError", error);
    } finally {
      this.isLoading = false;
      this.isReloading = false;
    }
  }

  public reloadData() {
    this.isReloading = true;
    return this.loadData();
  }

  public getBrandConfiguration(ignoreStored = false) {
    return this.$store.dispatch(
      `data/${DataModules.BRANDS}/getBrandConfiguration`,
      {
        ignoreStored,
        params: {
          keys: BrandConfigKeys.ALLOWED_UPLOAD_FILE_TYPES,
          brand_id: this.brandId
        },
        scope: this.brandConfigurationScope,
        vm: this
      }
    );
  }

  public get fileTypes() {
    return _.get(
      this.brandConfigurationData,
      BrandConfigKeys.ALLOWED_UPLOAD_FILE_TYPES,
      []
    );
  }

  public get hasValidAttachments() {
    return !_.find(this.files, file => {
      return (
        !this.fileTypes.includes(file.type) || this.hasWrongSize(file.size)
      );
    });
  }

  public removeAttachment(name: string) {
    this.files = _.filter(this.files, file => file.name !== name);
  }

  public async uploadFiles() {
    const newUploads = _.filter(this.attachments, ["isUploaded", false]);
    if (!newUploads.length) return Promise.resolve();
    try {
      await Promise.all(
        _.map(newUploads, newUpload => this.uploadFile(newUpload))
      );
    } catch (error) {
      if (this.$store.getters["api/is401"](error)) {
        await this.$store.commit("api/cancelRequests", this.requestPromises);
        await this.$store.dispatch("auth/refreshAccessToken");
        return this.uploadFiles();
      }

      throw error;
    }
  }

  public async uploadFile(newFile) {
    const attachmentIndex = _.findIndex(this.attachments, [
      "name",
      newFile.name
    ]);

    try {
      const data = new FormData();
      data.append("file", newFile.file);
      data.append("brand_id", this.brandId);
      this.attachments[attachmentIndex].isUploading = true;
      const result = await this.$store.dispatch(
        `data/${DataModules.TICKETS_FILES}/uploadFile`,
        {
          data,
          onUploadProgress: progressEvent => {
            this.attachments[attachmentIndex].progress = Math.floor(
              (progressEvent.loaded * 100) / progressEvent.total
            );
          },
          handle401Error: false,
          vm: this
        }
      );

      this.attachments[attachmentIndex] = _.merge(
        this.attachments[attachmentIndex],
        {
          progress: 0,
          isUploading: false,
          isUploaded: true,
          id: result[0].id,
          hash: result[0].hash
        }
      );
    } catch (error) {
      this.attachments[attachmentIndex] = _.merge(
        this.attachments[attachmentIndex],
        {
          progress: 0,
          isUploading: false
        },
        this.$store.getters["api/is401"](error)
          ? {}
          : {
              hasError: true,
              errorMessage: _.get(error, "response.data.error.data.file[0]")
            }
      );
      throw error;
    }
  }

  public clearFileUploader() {
    this.files = [];
  }

  public isAllowed(fileType: string) {
    return this.fileTypes.includes(fileType);
  }

  public hasWrongSize(fileSize: number) {
    return fileSize > 26214399;
  }

  // Methods
  public imageSrc(file) {
    return URL.createObjectURL(file);
  }

  @Watch("files")
  public onFilesChange() {
    this.attachments = _.filter(this.attachments, attachment => {
      return !!_.find(this.files, fileObj => fileObj.name === attachment.name);
    });

    _.forEach(this.files, file => {
      if (!_.find(this.attachments, ["name", file.name])) {
        this.attachments.push({
          name: file.name,
          isUploading: false,
          isUploaded: false,
          hasError: false,
          progress: 0,
          file,
          type: file.type,
          src: this.imageSrc(file)
        });
      }
    });
  }

  @Watch("hasValidAttachments")
  public onAttachmentChange(value) {
    this.$emit("isNotValid", !value);
  }

  public render(h: CreateElement) {
    // Handle loading
    if (this.handleLoading && this.isLoading)
      return h("is-loading", { props: { if: true }, attrs: this.loaderAttrs });
    // Handle request error
    if (this.handleRequestError && this.hasRequestError)
      return h("request-error", {
        props: { isLoading: this.isReloading },
        on: { click: () => this.reloadData() }
      });
    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $filesData: this.providerData(),
        $filesMethods: this.providerMethods
      })
    );
  }
}
